/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
    ITelemetry,
    ITelemetryModuleContext,
    ITrackDependency,
    LogLevel,
    TelemetryEvent,
    TelemetryEventContent,
    TelemetryLogOptions
} from '@msdyn365-commerce/telemetry-internal';
import { merge as _merge } from 'lodash';
import * as React from 'react';
import { ICommerceApiSettings, IModule } from '../contract';
import {
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICoreContext,
    IDictionary,
    IGeneric,
    IRequestContext,
    ISessionStorageContext
} from '../interfaces';
import { ICookieContext } from '../interfaces/context/ICookieContext';
import msdyn365Commerce, { MSDyn365Commerce } from '../msdyn365-commerce';
import { CultureInfoFormatter, getCurrencyCodeFromContext } from '../utilities';

declare let process: IGeneric<IAny>;

const renderModuleAttributes = (props: IGeneric<IAny>): IDictionary<string> => {
    if (!props || !props.id) {
        return {};
    }

    return { 'data-m-t': props.typeName };
};

/**
 * Creates a new mock telemetry object, takes a partial ITelemetry and fills in missing required properties
 * @param telemetry A partial or full implmentation of ITelemetry
 */
export const buildMockTelemetry = (telemetry?: Partial<ITelemetry>): ITelemetry => {
    const defaultMockTelemetry: ITelemetry = {
        trace: (message: string) => console.log(`[Mock Trace]: ${message}`),
        debug: (message: string, ...meta: IAny[]) => console.log(`[Mock Debug]: ${message}`, ...meta),
        information: (message: string) => console.log(`[Mock Info]: ${message}`),
        warning: (message: string) => console.log(`[Mock Warning]: ${message}`),
        error: (message: string, ...meta: IAny[]) => console.log(`[Mock Error]: ${message}`, ...meta),
        exception: (error: Error) => console.log(`[Mock Exception]: ${error}`),
        critical: (message: string) => console.log(`[Mock Info]: ${message}`),
        log: (logLevel: LogLevel, messageTemplate: string, logOptions?: TelemetryLogOptions) =>
            console.log('[Mock Log]: ', logLevel, messageTemplate, logOptions),
        logEvent: (eventType: TelemetryEvent, payload: TelemetryEventContent) => {
            // intentionally blank
        },
        trackMetric: (name: string, value: number) => console.log('[Mock Track Metric]: ', name, value),
        trackEvent: (name: string, properties, measurements) => console.log('[Mock Track Event]: ', properties, measurements),
        setTelemetryRequestContext: () => {
            return;
        },
        setTelemetryModuleContext: (moduleContext: ITelemetryModuleContext) => buildMockTelemetry,
        trackDependency: (options: ITrackDependency) => {
            console.log('[Mock Track Dependency]: ', options);
        },
        setTelemetryAttribute: (telemetryId: string, additionalTelemetryProperties: object) => {
            return { 'data-bi-id': '12345', 'data-m': '{"id":"12345","key1":"val1"}"' };
        }
    };
    return telemetry ? { ...defaultMockTelemetry, ...telemetry } : defaultMockTelemetry;
};

/**
 * Creates a mock CookieContext. Takes a partial ICookieContext and fills in required properties
 * @param context Partial action context.
 */
// eslint-disable-next-line complexity
export const buildMockCookieContext = (context?: Partial<ICookieContext>): ICookieContext => {
    return {
        get:
            (context && context.get) ||
            ((cookiename: string) => {
                return { name: cookiename, value: undefined, status: 'NOT_FOUND' };
            }),
        set:
            (context && context.set) ||
            (<T>(_cookieName: string, _cookieValue: T): void => {
                console.log('set called');
            }),
        remove:
            (context && context.remove) ||
            ((): void => {
                console.log('remove called');
            }),
        getCartCookie:
            (context && context.getCartCookie) ||
            ((): string => {
                console.log('getCartCookie called');
                return '';
            }),
        setCartCookie:
            (context && context.setCartCookie) ||
            ((): void => {
                console.log('setCartCookie called');
            }),
        getCheckoutCartCookie:
            (context && context.getCheckoutCartCookie) ||
            ((): string => {
                console.log('getCheckouCartCookie called');
                return '';
            }),
        setCheckoutCartCookie:
            (context && context.setCheckoutCartCookie) ||
            ((): void => {
                console.log('setCheckouCartCookie called');
            }),
        removeCheckoutCartCookie:
            (context && context.removeCheckoutCartCookie) ||
            ((): void => {
                console.log('removeCheckoutCartCookie called');
            }),
        getAccountSelectionCookie:
            (context && context.getAccountSelectionCookie) ||
            ((): string => {
                console.log('getAccountSelectionCookie called');
                return '';
            }),
        setAccountSelectionCookie:
            (context && context.setAccountSelectionCookie) ||
            ((): void => {
                console.log('setAccountSelectionCookie called');
            }),
        removeAccountSelectionCookie:
            (context && context.removeAccountSelectionCookie) ||
            ((): void => {
                console.log('removeAccountSelectionCookie called');
            }),
        isConsentGiven:
            (context && context.isConsentGiven) ||
            ((): boolean => {
                return true;
            }),
        setConsentCookie:
            (context && context.setConsentCookie) ||
            ((): void => {
                console.log('setConsentCookie called');
            }),
        deleteConsentCookie:
            (context && context.deleteConsentCookie) ||
            ((): void => {
                console.log('deleteConsentCookie called');
            }),
        getTargetIdCookie:
            (context && context.getCartCookie) ||
            ((): string => {
                console.log('getTargetIdCookie called');
                return '';
            })
    };
};

export const buildMockSessionStorageContext = (context?: Partial<ISessionStorageContext>): ISessionStorageContext => {
    return {
        getDataFromStorage:
            (context && context.getDataFromStorage) ||
            ((): object => {
                console.log('getDataFromStorage called');
                return {};
            }),
        setDataInStorage:
            (context && context.setDataInStorage) ||
            ((): void => {
                console.log('setDataInStorage called');
                return;
            }),
        getCheckoutCartData:
            (context && context.getCheckoutCartData) ||
            ((): object => {
                console.log('getCheckoutCartData called');
                return {};
            }),
        setCheckoutCartData:
            (context && context.setCheckoutCartData) ||
            ((): void => {
                console.log('setCheckoutCartData called');
                return;
            }),
        removeDataFromStorage:
            (context && context.removeDataFromStorage) ||
            ((): void => {
                console.log('removeDataFromStorage called');
                return;
            }),
        removeCheckoutCartData:
            (context && context.removeCheckoutCartData) ||
            ((): void => {
                console.log('removeCheckoutCartData called');
                return;
            })
    };
};

/**
 * Creates a mock IRequestContext. Takes a partial IRequestContext and fills in required properties
 * @param requestContext Partial IRequestContext
 */
export const buildMockRequest = (requestContext?: Partial<IRequestContext>): IRequestContext => {
    const mockRequest: IRequestContext = {
        url: {
            requestUrl: new URL('https://contoso.com'),
            staticCdnUrl: 'string'
        },
        canonicalDomain: 'contoso.com',
        canonicalUrl: 'https://contoso.com',
        locale: 'en-us',
        textDirection: 'ltr',
        params: {
            isDebug: false,
            isEditor: false,
            isPreview: false,
            isPerfAnalysis: false,
            concatJs: {
                hasValue: false,
                isTruthy: false,
                value: undefined
            },
            /**
             * Theme SCSS override matching registered theme name
             */
            theme: ''
        },
        device: {
            Type: 'pc'
        },
        user: {
            token: 'mockToken',
            isAuthenticated: false,
            optOutWebActivityTracking: false,
            isB2b: true
        },
        app: {},
        apiSettings: {
            baseUrl: 'mockBaseUrl',
            channelId: 1,
            eCommerceChannelId: '',
            oun: 'mockOUN',
            catalogId: 2,
            baseImageUrl: 'https://cms-ppe-imageresizer-mr.trafficmanager.net/cms/api/fabrikamsb/imageFileData/search?fileName=/',
            ratingsReviewsEndpoint: '',
            retailServerProxyVersion: '9.0.0',
            rnr: {
                proxyUrl: '',
                url: '',
                id: ''
            },
            imageResizerEndPoint: 'https://cms-ppe-imageresizer-mr.trafficmanager.net',
            imageResizerEndPointPreview: 'https://cms-ppe-imageresizer-mr.trafficmanager.net'
        },
        gridSettings: {
            xs: { w: 768 },
            sm: { w: 992 },
            md: { w: 1200 },
            lg: { w: 1600 },
            xl: { w: 1800 }
        },
        urlTokens: {},
        operationId: 'mockOperationId',
        siteTheme: 'default',
        // feature switches coming from server
        features: {},
        pageData: {},
        headers: {},
        cookies: buildMockCookieContext(),
        sessionStorage: buildMockSessionStorageContext(),
        themeOverride: {
            url: '',
            disableDefault: false
        },
        telemetrySettings: {
            eventHubAuthToken: '',
            eventHubEndPoint: '',
            optOutWebActivityTracking: false,
            operationId: 'mockOperationId',
            instrumentationKey: '',
            environmentId: 'Development',
            operationalInsightsInstrumentationKey: ''
        },
        telemetryData: {
            Checkout: [
                {
                    orderId: '',
                    cartId: '097504969642348742',
                    cartVersion: '12432',
                    Products: [
                        {
                            productChannelId: 18629762,
                            productChannelName: 'store',
                            productCategoryId: '9754',
                            productCategoryName: 'Sunglasses',
                            productId: '970374',
                            productName: 'Aviator',
                            productSku: 'special',
                            productPrice: 120,
                            productQuantity: 1,
                            productCurrency: 'USD'
                        }
                    ]
                }
            ],
            ProductPageView: {
                productChannelId: 18629762,
                productChannelName: '',
                productCategoryId: '',
                productCategoryName: '',
                productId: '08439484',
                productName: 'Awesome Tie',
                productSku: '',
                productPrice: 98,
                productQuantity: '',
                productCurrency: ''
            }
        },
        siteStylePreset: ''
    };
    return _merge(mockRequest, requestContext);
};

// eslint-disable-next-line react/display-name
const mockRenderView = (FunctionalComponent?: React.FC) => <T extends IModule>(props: T) => {
    if (!FunctionalComponent) {
        return null;
    }

    return React.createElement(FunctionalComponent, <any>props);
};

/**
 * Creates a mock IMSDyn365ActionContext. Takes a partial IActionContext and fills in required properties
 * @param context Partial action context.
 */
export const buildMockActionContext = (context?: Partial<IActionContext>): IActionContext => {
    return {
        chainAction:
            (context && context.chainAction) ||
            (async <T = unknown>(action: IAction<T>, input: IActionInput | IActionInput[]) => {
                return null;
            }),
        update:
            (context && context.update) ||
            ((input: IActionInput | IActionInput[], data: any): any => {
                return data;
            }),
        trace:
            (context && context.trace) ||
            ((message: string): void => {
                return;
            }),
        get:
            (context && context.get) ||
            ((cacheObjectType: string, cacheKey: string) => {
                return null;
            }),
        getAll:
            (context && context.getAll) ||
            ((cacheObjectType: string) => {
                return null;
            }),
        runAllActionsInQueue: async () => {
            return;
        },
        requestContext: buildMockRequest(context && context.requestContext),
        telemetry: buildMockTelemetry(context && context.telemetry),
        ...context
    };
};

/**
 * Creates a mock Core Context
 * @param coreContext Partial core context
 */
export const buildMockCoreContext = <TApp extends {} = {}>(coreContext: Partial<ICoreContext<TApp>>): ICoreContext<TApp> => {
    const request = buildMockRequest(coreContext.request);
    return {
        actionContext: buildMockActionContext(coreContext.actionContext),
        app: coreContext.app || <TApp>{},
        telemetry: buildMockTelemetry(coreContext.telemetry),
        request,
        cultureFormatter: coreContext.cultureFormatter || new CultureInfoFormatter(request.locale, getCurrencyCodeFromContext(request))
    };
};

/**
 * Creates a mock MSDyn365 App with fake API settings for use in testing
 */
export const initializeMockApp = (mockSettings?: ICommerceApiSettings) => {
    (<IGeneric<IAny>>MSDyn365Commerce).prototype.setMockSettings = function(): void {
        this._env = process.env.NODE_ENV;
        this._commerceApiSetting = mockSettings || {
            baseUrl: 'http://mock.cloud.retail.dynamics.com/',
            channelId: 1,
            catalogId: 0,
            oun: '1',
            baseImageUrl: `http://mock.cloud.retail.dynamics.com/MediaServer/`
        };
    };

    (<IGeneric<IAny>>msdyn365Commerce).setMockSettings();
};

/**
 * Generates a mock IModule<TData> prop
 * @param moduleData Mock TData that maps to props.data
 * @param dataActions Mock data actions that map to props.dataActions
 * @param moduleConfig Mock module configs that map to props.config
 * @param mockContext Mock ICoreContext that map to props.context
 * @param mockTelemetry Mock ITelemetry that map to props.telemetry. If no mock is provided a default will be passed that logs to console.
 */
export const buildMockModuleProps = <TData = IGeneric<IAny>, TConfig = IGeneric<IAny>>(
    moduleData: TData,
    dataActions: IGeneric<IAny>,
    moduleConfig?: TConfig,
    mockContext?: ICoreContext,
    mockTelemetry?: ITelemetry,
    app?: IGeneric<IAny>,
    view?: React.FC
): IModule<TData> => {
    mockContext = buildMockCoreContext(mockContext || {});
    return {
        context: mockContext,
        data: moduleData,
        dataActions: dataActions,
        config: moduleConfig || {},
        id: 'mock-id',
        friendlyName: 'Mock Id',
        typeName: 'mock-type',
        telemetry: buildMockTelemetry(mockTelemetry),
        app: { ...{ config: {} }, ...app },
        renderModuleAttributes: renderModuleAttributes,
        renderView: mockRenderView(view)
    };
};
